export default {
  methods: {
    // -----------------------------
    // Utility: Build option map
    // -----------------------------
    build_option_map(choices = []) {
      return choices.reduce((map, c) => {
        map[String(c.choice || "").toLowerCase()] = c.id;
        return map;
      }, {});
    },

    // -----------------------------
    // Helpers for dependent question
    // -----------------------------
    _get_dep_question_data(dq, all_questions_map) {
      return all_questions_map[dq.dependant_section_question_id];
    },

    _get_dep_value(dep_question_data, dep_option_map) {
      if (!dep_question_data) return null;
      return this.deserialize_answer(
        dep_question_data.answer,
        dep_question_data?.question_type?.question_type || "short_text",
        dep_option_map
      );
    },

    _build_options(dq, dependantType) {
      const isSelectable = ["multi_select", "single_select"].includes(
        String(dependantType || "").toLowerCase()
      );

      //IMPORTANT: value is TEXT (choice) so trigger_condition like "Online" matches
      return isSelectable && dq.multi_select_choices
        ? dq.multi_select_choices.map((c) => ({
            text: c.choice,
            value: c.choice,
          }))
        : [];
    },

    _get_dep_id(dependent_question) {
      return dependent_question?.dependant_section_question_id ?? null;
    },

    _get_question_by_dep_id(all_questions_map, dep_id) {
      return Reflect.get(all_questions_map || {}, dep_id) || null;
    },

    _resolve_choices(question, dependent_question) {
      return question?.multi_select_choices || dependent_question?.multi_select_choices || [];
    },

    _resolve_raw_type(question, dependent_question) {
      return (
        question?.question_type?.question_type ||
        dependent_question?.dependant_question_type?.question_type ||
        "short_text"
      );
    },

    _resolve_answer(question, dependent_question) {
      return question?.answer ?? dependent_question?.answer ?? null;
    },

    _resolve_label(question, dependent_question) {
      return question?.question_text || dependent_question?.dependant_question_text || "";
    },

    _resolve_required(question, dependent_question) {
      return (question?.is_mandatory ?? dependent_question?.is_mandatory) === true;
    },

    _build_child_dependents(dep, all_questions_map, node_key) {
      return (dep?.dependant_questions || [])
        .map((child) => this.build_dependent_question(child, all_questions_map, node_key))
        .filter(Boolean);
    },

    // -----------------------------
    // Main: Build dependent question (RECURSIVE)
    // -----------------------------
    build_dependent_question(dependent_question, all_questions_map, parent_key = "root") {
      const dep_id = this._get_dep_id(dependent_question);
      if (!dep_id) return null;

      const question = this._get_question_by_dep_id(all_questions_map, dep_id);

      const choices = this._resolve_choices(question, dependent_question);
      const option_map = this.build_option_map(choices);

      const raw_type = this._resolve_raw_type(question, dependent_question);
      const answer = this._resolve_answer(question, dependent_question);

      const value = this.deserialize_answer(answer, raw_type, option_map);
      const node_key = `${parent_key}.${dep_id}`;

      const data = {
        node_key,
        label: this._resolve_label(question, dependent_question),
        model: `q_${dep_id}`,
        id: `q_${dep_id}`,
        section_question_id: dep_id,
        type: this.map_question_type(raw_type),
        required: this._resolve_required(question, dependent_question),
        trigger_condition: dependent_question?.trigger_condition ?? null,
        is_visible: false,
        options: choices.map((c) => ({ text: c.choice, value: c.choice })),
        value,
        is_dependent: true,
        dependent_questions: this._build_child_dependents(dependent_question, all_questions_map, node_key),
      };
      
      return data;
    },





    // -----------------------------
    // Answer deserialization
    // -----------------------------
    deserialize_answer(answer, question_type, option_map) {
      if (!answer) {
        return question_type === "multi_select" || question_type === "checkboxes" ? [] : "";
      }
      const type_key = String(question_type || "").toLowerCase();
      const deserializer = this._get_deserializer(type_key);
      return deserializer(answer, option_map);
    },

    _get_deserializer(type_key) {
      const deserializers = {
        single_select: (answer) => this._deserialize_select(answer),
        radio: (answer) => this._deserialize_select(answer),
        multi_select: (answer) => this._deserialize_multi_select(answer),
        checkboxes: (answer) => this._deserialize_multi_select(answer),
        file: (answer) => this._deserialize_file(answer),
        facial_verification: (answer) => this._deserialize_file(answer),
      };
      return Reflect.get(deserializers, type_key) || ((a) => a);
    },

    _deserialize_select(answer) {
      // Keep as text so it matches trigger_condition like "Yes"
      return answer ?? "";
    },

    _deserialize_multi_select(answer) {
      // Keep as array of text values
      if (!answer) return [];
      return String(answer)
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);
    },


    _deserialize_file(answer) {
      if (typeof answer === "object" && answer && Reflect.has(answer, "file_name")) return answer;
      return typeof answer === "string" ? { file_name: answer } : null;
    },

    // -----------------------------
    // Dependent question listeners
    // -----------------------------
    setup_dependent_question_listeners() {
      if (!this.safe_form_config.sections) return;

      this.safe_form_config.sections.forEach((section) => {
        section.fields.forEach((field) => {
          // watch top-level field
          if (field.dependent_questions?.length > 0) {
            this.$watch(
              () => Reflect.get(this.safe_form_data, field.model),
              (newValue) => {
                this.update_dependent_questions_visibility(field, newValue);
              },
              { deep: true }
            );

            // initial visibility
            this.update_dependent_questions_visibility(
              field,
              Reflect.get(this.safe_form_data, field.model)
            );
          }

          // ALSO watch each dependent question recursively
          this._watch_dependent_tree(field.dependent_questions || []);
        });
      });
    },

    _watch_dependent_tree(dep_list = []) {
      (dep_list || []).filter(Boolean).forEach((dependent_question) => {
        this.$watch(
          () => Reflect.get(this.safe_form_data, dependent_question.model),
          (newValue) => this.update_dependent_questions_visibility(dependent_question, newValue),
          { deep: true }
        );

        this.update_dependent_questions_visibility(dependent_question, Reflect.get(this.safe_form_data, dependent_question.model));

        if (dependent_question.dependent_questions?.length) {
          this._watch_dependent_tree(dependent_question.dependent_questions);
        }
      });
    },


    // -----------------------------
    // Visibility (RECURSIVE) + correct matching
    // -----------------------------
    update_dependent_questions_visibility(questionNode, currentValue) {
      if (!questionNode.dependent_questions?.length) return;

      questionNode.dependent_questions.forEach((child) => {
        const shouldShow = this.does_value_match_trigger(
          currentValue,
          child.trigger_condition,
          questionNode //pass CURRENT node (its options)
        );

        child.is_visible = shouldShow;

        // if parent hides, hide all descendants too
        if (!shouldShow && child.dependent_questions?.length) {
          this._hide_descendants(child);
        }

        // if parent shows, re-evaluate child’s children based on current child value
        if (shouldShow && child.dependent_questions?.length) {
          const childValue = Reflect.get(this.safe_form_data, child.model);
          this.update_dependent_questions_visibility(child, childValue);
        }
      });
    },

    _hide_descendants(node) {
      (node.dependent_questions || []).forEach((c) => {
        c.is_visible = false;
        if (c.dependent_questions?.length) this._hide_descendants(c);
      });
    },

    does_value_match_trigger(value, triggerCondition) {
      if (!triggerCondition) return false;

      // Since we set option.value = option.text, the value will already be "Yes"/"Online"/etc.
      if (Array.isArray(value)) {
        return value.some(
          (v) => this.normalize_value(v) === this.normalize_value(triggerCondition)
        );
      }

      return this.normalize_value(value) === this.normalize_value(triggerCondition);
    },
  },
};
